import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import PopularRentalCarsCarousel from "./PopularRentalCarsCarousel";
import PopularRentalCarCard from "./PopularRentalCarCard";
import PopularRentalCarsCampaign from "./PopularRentalCarsCampaign";
import PopularRentalCarsAllCompanies from "./PopularRentalCarsAllCompanies";
import PopularRentalCarsRentNow from "./PopularRentalCarsRentNow";
import { useIsMobile } from "@/hooks/useIsMobile";
import { Button } from "@/components/ui/Button";
import TabSwitcher from "@/components/TabSwitcher";
import Modal from "@/components/ui/Modal";
import CarSearchFormWrapper from "@/components/pages/car/searchForm";
import { useModal } from "@/contexts/ModalContext";
import {
  usePopularRentalCarAnalytics
} from "@/components/pages/car/sections/popular-rental-cars/usePopularRentalCarAnalytics";

export default function PopularRentalCars(props) {
  const { data, searchProps, campaign, isCompanyPage } = props;
  const { cars, locationType } = data;

  const t = useTranslationDetector();
  const isMobile = useIsMobile();
  const [activeClass, setActiveClass] = useState("");
  const [carList, setCarList] = useState([]);
  const modal = useModal("popular-cars-search-modal");

  const { resetViewedItems, trackSelectItem } = usePopularRentalCarAnalytics({
    items: carList,
    locationType
  });

  const vehicleClasses = useMemo(() => {
    return cars
      .map((v) => v.Class)
      .filter((value, index, array) => {
        return array.indexOf(value) === index;
      })
      .map((value) => ({
        id: value,
        label: t(`Car.${value}`),
      }));
  }, [t, cars]);

  useEffect(() => {
    setActiveClass(vehicleClasses?.[0]?.id);
  }, [vehicleClasses]);

  useEffect(() => {
    resetViewedItems();

    const activeClassCars = cars
      .filter((c) => c.Class === activeClass)
      .slice(0, 10);

    setCarList(activeClassCars);
  }, [activeClass]);

  const handleClick = useCallback((carItem) => {
    trackSelectItem(carItem);
    modal.open();
  }, [activeClass]);

  const placeHolderComponent = useMemo(() => {
    if (isCompanyPage) {
      return <PopularRentalCarsAllCompanies />;
    }

    return <PopularRentalCarsRentNow handleClick={handleClick} />;
  }, [isCompanyPage]);

  return (
    <div className="">
      <div className="mt-4 w-full md:mt-8">
        <ul className="scrollbar-hide flex w-full overflow-auto">
          <TabSwitcher
            tabs={vehicleClasses}
            selected={activeClass}
            onChange={setActiveClass}
          />
        </ul>
      </div>

      {carList.length === 1 && (
        <>
          <div className="mt-4 flex items-center gap-6 md:mt-8">
            <PopularRentalCarCard
              index={0}
              car={carList[0]}
              handleClick={handleClick}
              horizontal
            />

            <div className="flex flex-1 flex-col items-center justify-center gap-1 self-stretch rounded-2xl bg-[#F5F6F7] p-12 max-md:hidden">
              {campaign ? (
                <PopularRentalCarsCampaign
                  searchProps={searchProps}
                  activeClass={activeClass}
                  campaign={campaign}
                />
              ) : (
                placeHolderComponent
              )}
            </div>
          </div>
          <div className="mt-4 flex justify-center md:mt-6">
            <Button className="select-none" onClick={handleClick}>
              {t("Car.RentNowHome")}
            </Button>
          </div>
        </>
      )}

      {carList.length === 2 &&
        (isMobile ? (
          <PopularRentalCarsCarousel
            activeClass={activeClass}
            handleClick={handleClick}
            carList={carList}
          />
        ) : (
          <>
            <div className="mt-4 flex items-center gap-6 md:mt-8">
              <PopularRentalCarCard
                index={0}
                car={carList[0]}
                handleClick={handleClick}
                horizontal
              />

              <PopularRentalCarCard
                index={1}
                car={carList[1]}
                handleClick={handleClick}
                horizontal
              />
            </div>
            <div className="mt-4 flex justify-center md:mt-6">
              <Button className="select-none" onClick={handleClick}>
                {t("Car.RentNowHome")}
              </Button>
            </div>
          </>
        ))}

      {carList.length > 2 && (
        <PopularRentalCarsCarousel
          key={activeClass}
          activeClass={activeClass}
          handleClick={handleClick}
          carList={carList}
        />
      )}

      <Modal
        id="popular-cars-search-modal"
        size="full"
        title={t("Car.rentModalHomeTitle")}
        headerClassName="border-none"
        className="!bg-background-global overflow-visible md:!max-w-[86rem]"
        contentClassName="flex-1 p-6 md:px-6 md:pt-4 md:pb-5"
        isBottomSheet
      >
        <CarSearchFormWrapper
          hidePnrSection
          className="z-[999]"
          id="modal-search"
          hideCampaignListener
          defaultFilters={{ VehicleClass: activeClass }}
          viewportHeightWrapperActive
        />
      </Modal>
    </div>
  );
}
